import React, { useEffect, useState, useRef } from "react";
import { Button, Col, Container, Form, InputGroup, Nav, Row, Tab } from "react-bootstrap";
import axios from "axios";
import get from "lodash.get";
import { injectIntl } from "react-intl";
import PropTypes from 'prop-types';
import { isEmpty } from "lodash";
import { useDebouncedCallback } from "use-debounce";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

// Services
import { userRequestHeaders } from '../../services/user';
import { toggleLoading, validate } from "../../services/helpers";
import { jobFetchJob, jobSetGovidJob } from "../../services/job";

// Components
import RetrieveInvoiceModal from "../../components/RetrieveInvoiceModal";
import { far } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import messages from "./Dashboard.messages";
import useUserAccount from "../../hooks/useUserAccount";
import DashboardHeader from "../../components/DashboardHeader";
import OverpaidWizard from "../../components/OverpaidWizard";
import useSessionStorage from "../../hooks/useSessionStorage";
import useJobs from "../../hooks/useJobs";
import useSettings from "../../hooks/useSettings";
import DashboardPane from "../../components/DashboardPane";
import useEnv from "../../hooks/useEnv";
import useFormatter from "../../hooks/useFormatter";

const Dashboard = (props) => {
    const { getEnv } = useEnv();
    const { formatCurrency } = useFormatter();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { uuid } = useParams();
    const {
        userIsCompany, userIsAuthenticated, userIsLoggedIn, userShouldSwitchToCompany, userNeedsIdentityVerification, uuidAuthenticate
    } = useUserAccount();
    const userAuthorized = userIsAuthenticated();
    const { showChatDisabledNotice } = useSettings();
    const [showRetrieveModal, setShowRetrieveModal] = useState(false);
    const [retrieveInvoiceError, setRetrieveInvoiceError] = useState({
        message: '',
        type: 'light'
    });
    const [dashboardMessage, setDashboardMessage] = useState({
        message: '',
        type: 'light'
    });
    const [govidJobs, setGovidJobs] = useState(false);
    const [currentTab, setCurrentTab] = useState('');
    const [searchText, setSearchText] = useState("");
    const [focusJobId, setFocusJobId] = useState('');
    const jobRefs = useRef(new Map()); // jobid -> invoice card element, for scrolling to a selected job
    const [currentSearchText, setCurrentSearchText] = useState("");
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [showOverpaidWizard, setShowOverpaidWizard] = useState(false);
    const [overpaidJob, setOverpaidJob] = useState(null);
    const [overpaidInformation, setOverpaidInformation] = useState({
        show: false,
        account: '',
        amount: '',
        name: '',
        fee: ''
    });
    const searchResultsRef = useRef(null); // Ref for scrolling to search results
    const { getjobTokenUuid, clearjobTokenUuid } = useSessionStorage();
    const { fetchJobWithUuid } = useJobs();
    const jobTokenUuid = getjobTokenUuid();

    /**
     * Handle search parameter changes:
     * - Invoice loading
     * - Active tab update
     */
    useEffect(() => {
        // Handle uuid in separate effect
        // Do nothing if user should switch to company
        // Do nothing if user should agree to consent and proceed to identity verification
        if (uuid || userShouldSwitchToCompany || userNeedsIdentityVerification) {
            return;
        }
        const tab = searchParams.get('t') ?? 'invoices';
        const search = searchParams.get('s') ?? '';
        const jobId = searchParams.get('j') ?? null;
        if (['invoices', 'archive', 'search'].includes(tab) && tab !== currentTab) {
            setCurrentTab(tab);
            if (tab !== 'search') {
                getOpenInvoicesByGovid(tab).catch(error => setGovidJobs(error));
            }
        }
        if (search !== currentSearchText) {
            setCurrentSearchText(search);
            setSearchText(search);
            if (search.length > 3) {
                getOpenInvoicesByGovid('search', search, true).catch(error => setGovidJobs(error));
                handleTabChange('search');
            } else if (search.length === 0) {
                handleTabChange('invoices');
            }
        }
        if (jobId !== focusJobId && focusJobId !== null) {
            setFocusJobId(jobId);
        }
    }, [searchParams, uuid, userShouldSwitchToCompany, userNeedsIdentityVerification, focusJobId]);

    /**
     * Scroll to a specific job's invoice card once it has rendered
     * (i.e. once its ref has been registered by DashboardPane)
     */
    useEffect(() => {
        if (focusJobId === null) {
            return;
        }
        const target = jobRefs.current.get(String(focusJobId));
        if (target) {
            target.scrollIntoView({ behavior: 'smooth' });
            setFocusJobId(null);
        }
    }, [focusJobId, govidJobs]);

    /**
     * Register or unregister an invoice card's element so it can be scrolled to later
     * @param {number|string} jobid
     * @param {HTMLElement|null} element
     */
    const registerJobRef = (jobid, element) => {
        const key = String(jobid);
        if (element) {
            jobRefs.current.set(key, element);
        } else {
            jobRefs.current.delete(key);
        }
    }

    // Govid search with debounced timeout
    const debouncedSearch = useDebouncedCallback((value) => {
        setSearchParams((params) => {
            params.set('s', value);
            params.delete('j');
            return params;
        });
    }, props.timeout ?? 1000);

    /**
     * Function for closing the Return payment wizard
     */
    const returnPaymentClose = () => {
        resetRetrieveInvoiceError();
        setShowOverpaidWizard(false);
        setOverpaidJob(null);
    };

    /**
     * Return payment wizard step 1
     * @param formData
     */
    const handleOverpaidJobSearch = (formData) => {
        // Reset errors and other notifications
        resetRetrieveInvoiceError();
        resetDashboardMessage();
        resetOverpaidInformation();

        // Get the form
        const form = document.getElementById('returnpayment-dpid-form');
        if (form !== undefined && form !== null && validate(form)) {
            // Get the case number and Online ID and form the dpid
            const { caseNumber, onlineId  } = formData;
            if (caseNumber !== '' && onlineId !== '') {
                // Fetch the job data and move to next step
                void handleSubmit(`${caseNumber}-${onlineId}`);
            } else {
                setRetrieveInvoiceError({
                    message: lng(messages.unknownError),
                    type: 'danger'
                });
            }
        } else {
            setRetrieveInvoiceError({
                message: lng(messages.unknownError),
                type: 'danger'
            });
        }
    };

    // Translations function
    const lng = props.intl.formatMessage;

    // Get open invoices by user govid
    const getOpenInvoicesByGovid = async (eventType = 'invoices', searchTerm = '', scroll = false) => {
        setGovidJobs(false);

        // Get header information that might contain authorization details
        const headers = userRequestHeaders();

        let _govidJobs = false;
        let route = '/govidsearch';
        let queryParams = '';

        // Use different route for fetching invoices for non-authorized users
        if (eventType === 'search' && !userAuthorized) {
            eventType = 'linksearch';
        }

        switch (eventType) {
            case "invoices":
                queryParams = '?include_paid=0&exclude_open=0&order=paydate&dir=DESC';
                break;
            case "archive":
                queryParams = '?include_paid=1&exclude_open=1&order=paydate&dir=DESC';
                break;
            case "search":
                queryParams = '?include_paid=1&exclude_open=0&order=paydate&dir=DESC&search=' + searchTerm;
                break;
            case "linksearch":
                route = '/linksearch';
                queryParams = '?include_paid=1&exclude_open=0&order=paydate&dir=DESC&search=' + searchTerm;
                break;
        }

        await axios({
            method: 'GET',
            url: `${getEnv('API_URL')}/online${route}${queryParams}`,
            headers: headers
        })
        .then(function (res) {

            // Could be empty body with 204 no content
            if (typeof res.data !== 'undefined' && typeof res.data.govidjob !== 'undefined' && res.data.govidjob !== null) {
                // set jobs
                setGovidJobs(res.data.govidjob);

                _govidJobs = res.data.govidjob;

                // save govid jobs to session storage, too
                jobSetGovidJob(_govidJobs);

                // scroll to the search results only when applicable
                if (scroll && searchResultsRef.current && focusJobId === null) {
                    searchResultsRef.current.scrollIntoView({ behavior: 'smooth' });
                }
            }
        })
        .catch(function (error) {
            if (error.response?.status === 401) {
                const alert = {message: lng(messages.authError), type: 'danger'};
                navigate("/logout", { state: { alert } });
            } else {
                setGovidJobs(error);
                _govidJobs = error;
                return Promise.reject(error);
            }
        })
        .then(function () {
            // Add empty array if no jobs found
            if (_govidJobs === false) {
                setGovidJobs([]);
            }
        });
    }

    useEffect(() => {
        const handleDpidSearch = (dpid) => {
            const [jobid, code] = dpid.split('-');
            setIsAuthenticating(false);
            toggleLoading(false);
            sessionStorage.removeItem('dpid');
            void handleSubmit(`${jobid}-${code}`);
        }
        // get dpid from session storage if user identified with an undirect link
        const dpid = sessionStorage.getItem('dpid');
        const authenticateWithUuid = async () => {
            try {
                await uuidAuthenticate(uuid);

                if (dpid !== null) {
                    // redirect the user back to page without uuid parameter
                    navigate("/jobsearch");
                    handleDpidSearch(dpid);
                    getOpenInvoicesByGovid().catch(error => {
                        setGovidJobs(error);
                    });
                } else if (jobTokenUuid) {
                    // Not sure when this would be used together with uuid param, but just in case
                    void handleSubmit(jobTokenUuid, true);
                    clearjobTokenUuid();
                } else {
                    // redirect the user back to page without uuid parameter
                    navigate("/jobsearch");
                    toggleLoading(false);

                    // User has identificated, allow access to / to display the page
                    setIsAuthenticating(false);
                }
            } catch {
                // token request failed, redirecting back to indentification
                navigate("/logout");
                toggleLoading(false);
            }
        }
        // if uuid exists, first authenticate the user
        if (uuid) {
            // loading animation on
            toggleLoading(true, lng(messages.openingInvoice) + "...");
            void authenticateWithUuid();
        } else if (userIsLoggedIn()) {
            // Allow to display the page
            setIsAuthenticating(false);

            if (dpid !== null && !userShouldSwitchToCompany) {
                handleDpidSearch(dpid);
            } else if (jobTokenUuid && !userShouldSwitchToCompany) {
                void handleSubmit(jobTokenUuid, true);
                clearjobTokenUuid();
            }
        }

    }, []);

    /**
     * Handler for job row click
     *
     * @param event
     * @param {string} dpid
     */
    const handleJobRowClick = (event, dpid) => {
        event.preventDefault();
        if (!isEmpty(dpid)) {
            const [jobid, code] = dpid.split('-');
            void handleSubmit(`${jobid}-${code}`);
        }
    }

    const handleBankAccountSubmitSuccess = (dpid) => {
        void handleSubmit(dpid);
    }

    /**
     * Handle a failed job search: show a message or redirect depending on the error
     *
     * @param error
     * @param useUuid
     */
    const handleSubmitError = (error, useUuid) => {
        toggleLoading(false);
        // request failed completely
        if (error?.response === undefined) {
            setRetrieveInvoiceError({
                message: lng(messages.pleaseTryAgain),
                type: 'danger'
            });
            return;
        }

        // response exists
        if (error.response.status === 401) {
            // authentication failed, redirect back to authentication
            const alert = { message: lng(messages.authError), type: 'danger' };
            navigate("/logout", { state: { alert } });
        } else if (error.response.status === 403) {
            // Forbidden messages
            let message = lng(messages.forbiddenError);
            const errorMessage = error.response.data?.Message;

            if (errorMessage === 'Online access log - user access denied') {
                message = lng(messages.searchLocked);
            } else if (errorMessage === 'Online jobsearch - company person is not allowed to sign') {
                message = lng(messages.notAllowedToSign);
            }

            setRetrieveInvoiceError({
                message: message,
                type: 'danger'
            });
        } else if (useUuid) {
            // Invoice not found via link — show top-level info, clean URL, load govid jobs
            setDashboardMessage({ message: lng(messages.invoiceNotFoundFromLink), type: 'info' });
            navigate("/jobsearch");
            getOpenInvoicesByGovid().catch(govidError => {
                setGovidJobs(govidError);
            });
        } else {
            setRetrieveInvoiceError({
                message: lng(messages.jobNotFound),
                type: 'danger'
            });
        }
    }

    /**
     * Submit the search form, and redirect to job (also used for govid search)
     *
     * @param query
     * @param useUuid
     */
    const handleSubmit = async (query, useUuid = false) => {
        // Reset errors and other notifications
        resetRetrieveInvoiceError();
        resetDashboardMessage();
        resetOverpaidInformation();

        toggleLoading(true, lng(messages.searchingForJob));

        // API call GET /online/jobsearch/{dpid}, jobservice fetches the job details and saves it to storage for later use
        try {
            const job = await (useUuid ? fetchJobWithUuid(query) : jobFetchJob(query));
            toggleLoading(false);
            // Ensure the Retrieve invoice modal is closed
            setShowRetrieveModal(false);
            // Check if job is overpayment
            if (job?.statuscode === 302) {
                // Check if bank account number has already been notified
                if (job?.modoverpaid === true) {
                    setOverpaidJob(job);
                    setShowOverpaidWizard(true);
                } else {
                    setOverpaidInformationFromJob(job);
                    returnPaymentClose();
                }
            } else if (job !== null && job !== undefined) {
                // If job status is not overpayment, redirect to job's page
                navigate("/job", { state: { searchText, currentTab, jobId: job.jobid } });
            } else {
                setRetrieveInvoiceError({
                    message: lng(messages.pleaseTryAgain),
                    type: 'danger'
                });
            }
        } catch (error) {
            handleSubmitError(error, useUuid);
        }
    }

    /**
     * Get overpaid information for job with notified account number
     * and set it to be displayed to user
     * @param {object} job
     */
    const setOverpaidInformationFromJob = (job) => {
        // Get locale and currency for formatting
        let currency = get(job, 'currency', false);
        currency = currency && currency !== '' ? currency : getEnv('DEFAULT_CURRENCY');

        setOverpaidInformation({
            show: true,
            account: job.overpaid_iban,
            amount: formatCurrency(job.modoverpaidamount, currency),
            name: job.overpaid_name,
            fee: formatCurrency(job.modoverpaidfee, currency)
        })
    }

    /**
     * Reset overpaid information
     */
    const resetOverpaidInformation = () => {
        setOverpaidInformation({
            show: false,
            account: '',
            amount: '',
            name: '',
            fee: ''
        });
    }

    /**
     * Reset general dashboard message
     */
    const resetDashboardMessage = () => {
        setDashboardMessage({
            message: '',
            type: 'light'
        });
    }

    /**
     * Reset invoice retrieving errors
     */
    const resetRetrieveInvoiceError = ()=> {
        setRetrieveInvoiceError({
            message: '',
            type: 'light'
        });
    }

    /**
     * Toggle retrieve invoice modal visibility and clear its errors
     */
    const retrieveModalToggle = () => {
        resetRetrieveInvoiceError();
        resetDashboardMessage();
        resetOverpaidInformation();
        setShowRetrieveModal(!showRetrieveModal);
    }

    /**
     * Toggle overpaid wizard visibility and clear its errors
     */
    const overpaidWizardToggle = () => {
        resetRetrieveInvoiceError();
        resetDashboardMessage();
        resetOverpaidInformation();
        if (showOverpaidWizard) {
            setOverpaidJob(null);
        }
        setShowOverpaidWizard(!showOverpaidWizard);
    }

    /**
     * Handle search input events
     * @param event
     */
    const handleSearch = (event) => {
        setSearchText(event.target.value);
        debouncedSearch(event.target.value);
    }

    const handleTabChange = (tab) => {
        if (currentTab === tab) {
            return;
        }
        setSearchParams(params => {
            params.set('t', tab);
            return params;
        })
    }

    if (isAuthenticating || userShouldSwitchToCompany) {
        return <></>
    }

    const searchFieldElement = (
        <Col xl={6} md={12} className="order-1 order-xl-2">
            <InputGroup className="has-search">
                <InputGroup.Text className="form-control-search" id="search-icon">
                    <FontAwesomeIcon className="text-body-tertiary" icon={far.faMagnifyingGlass} />
                </InputGroup.Text>
                <Form.Control
                    placeholder={lng(messages.searchFieldPlaceholder)}
                    aria-label={lng(messages.searchFieldPlaceholder)}
                    aria-describedby={lng(messages.searchFieldPlaceholder)}
                    onChange={handleSearch}
                    value={searchText}
                    id="dashboard-search-input"
                    type="search"
                    autoComplete="off"
                    data-testid="dashboard-search-input"
                />
            </InputGroup>
        </Col>
    )

    const tabsElement = (
        currentTab === "search" ? (
            <Nav
                fill
                defaultActiveKey="search"
                className="online-tabs"
                role="tablist"
                aria-label={lng(messages.searchResults)}
            >
                <Nav.Item>
                    <Nav.Link
                        eventKey="search"
                    >
                        {lng(messages.searchResults)}
                    </Nav.Link>
                </Nav.Item>
            </Nav>
        ) : (
            <Nav
                justify
                defaultActiveKey="invoices"
                className="online-tabs"
                role="tablist"
                aria-label={lng(messages.tabInvoicesTitle) + " & " + lng(messages.tabArchiveTitle)}
            >
                <Nav.Item>
                    <Nav.Link
                        eventKey="invoices"
                        id="tab-invoices"
                        aria-controls="tabpanel-invoices"
                        aria-selected={currentTab === "invoices"}
                        tabIndex={0}
                        role="tab"
                    >
                        {lng(messages.tabInvoicesTitle)}
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link
                        eventKey="archive"
                        id="tab-archive"
                        aria-controls="tabpanel-archive"
                        aria-selected={currentTab === "archive"}
                        tabIndex={0}
                        role="tab"
                    >
                        {lng(messages.tabArchiveTitle)}</Nav.Link>
                </Nav.Item>
            </Nav>
        )
    )

    const tabContentElement = (
        <Tab.Content>
            <DashboardPane
                current={currentTab === 'invoices'}
                jobs={govidJobs}
                type="invoices"
                onClick={handleJobRowClick}
                registerJobRef={registerJobRef}
                emptyResultMessage={userIsCompany() ? lng(messages.noOpenCompany) : lng(messages.noOpen)}
            />
            <DashboardPane
                current={currentTab === 'archive'}
                jobs={govidJobs}
                type="archive"
                onClick={handleJobRowClick}
                registerJobRef={registerJobRef}
                emptyResultMessage={userIsCompany() ? lng(messages.noOpenCompany) : lng(messages.noArchivedInvoices)}
            />
            <DashboardPane
                current={currentTab === 'search'}
                jobs={govidJobs}
                type="search"
                onClick={handleJobRowClick}
                registerJobRef={registerJobRef}
                emptyResultMessage={userIsCompany() ? lng(messages.noOpenCompany) : lng(messages.noSearchResults)}
            />
        </Tab.Content>
    )

    return (
        <div className='content flex-fill'>
            <title>{`${lng(messages.searchTitle)} | MyRopo`}</title>
            <Container fluid id='dashboard' className='p-0 d-flex h-100'>
                <Container className="p-4 pb-0">
                    <main id="main">
                        {userAuthorized && (
                            <DashboardHeader
                                dashboardMessage={dashboardMessage}
                                overpaidInformation={overpaidInformation}
                                retrieveModalToggle={retrieveModalToggle}
                                overpaidWizardToggle={overpaidWizardToggle}
                                showChatDisabledNotice={showChatDisabledNotice}

                            />
                        )}
                        <Tab.Container
                            id="govidsearch"
                            defaultActiveKey="invoices"
                            activeKey={currentTab}
                            onSelect={(key) => handleTabChange(key)}
                        >
                            <Row className={userAuthorized ? "pt-4" : "pt-md-4"}>
                                <Col xl={userAuthorized ? 6 : 12} md={12} className="order-2 order-xl-1 mt-4 mt-xl-0 online-tabs">
                                    {tabsElement}
                                </Col>
                                <div className='d-md-none' ref={searchResultsRef} />
                                {userAuthorized && searchFieldElement}
                            </Row>
                            <Row>
                                <Col>
                                    {tabContentElement}
                                </Col>
                            </Row>
                        </Tab.Container>
                        {/* Users with direct link see log in button */}
                        {!userAuthorized && (
                            <Row className="sticky-bottom pb-3">
                                <Col xs={12} xl={{ span: 6, offset: 3 }} className="d-grid ">
                                    <Button variant="primary" onClick={() => navigate("/user/logout")}>{lng(messages.logInForAllInvoices)}</Button>
                                </Col>
                            </Row>
                        )}
                    </main>
                </Container>
                <RetrieveInvoiceModal
                    show={showRetrieveModal}
                    hide={retrieveModalToggle}
                    handleSubmit={handleSubmit}
                    error={retrieveInvoiceError}
                    {...props}
                />
                <OverpaidWizard
                    open={showOverpaidWizard}
                    onClose={overpaidWizardToggle}
                    job={overpaidJob}
                    onJobSearchSubmit={handleOverpaidJobSearch}
                    showSearchStep={true}
                    jobSearchError={retrieveInvoiceError}
                    onBankAccountSubmitSuccess={handleBankAccountSubmitSuccess}
                />
            </Container>
        </div>
    );
};

Dashboard.propTypes = {
    'intl': PropTypes.object.isRequired,
    'timeout': PropTypes.number,
};

export default injectIntl(Dashboard);
